import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #cartRefreshVersion = 0;
  #cartRefreshController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#refreshCart);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#refreshCart);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#cartRefreshVersion++;
    this.#cartRefreshController?.abort();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handlePageShow = (event) => {
    if (event.persisted) this.#refreshCart();
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#refreshCart();
  };

  #refreshCart = async () => {
    if (document.visibilityState === 'hidden' || !this.isConnected) return;

    const sectionId = this.querySelector('cart-items-component')?.dataset.sectionId;
    if (!sectionId) return;

    const version = ++this.#cartRefreshVersion;
    this.#cartRefreshController?.abort();
    const controller = new AbortController();
    this.#cartRefreshController = controller;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      const result = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!result.ok) throw new Error(`Cart refresh failed: ${result.status}`);
      const html = await result.text();
      if (!this.isConnected || version !== this.#cartRefreshVersion) return;

      const response = new DOMParser().parseFromString(html, 'text/html');
      const newSection = response.getElementById(`shopify-section-${sectionId}`);
      const newItems = newSection?.querySelector('cart-items-component');
      const newDialog = newSection?.querySelector('cart-drawer-component dialog');
      if (!newItems || !newDialog) throw new Error('Cart refresh response is incomplete');

      const count = Number.parseInt(newItems.querySelector('[ref="cartItemCount"]')?.textContent ?? '0', 10);
      if (!Number.isFinite(count) || count < 0) throw new Error('Cart refresh count is invalid');

      await morphSection(sectionId, html, { mode: 'hydration' });
      if (!this.isConnected || version !== this.#cartRefreshVersion) return;

      const icon = this.querySelector('cart-icon');
      if (icon && 'renderCartBubble' in icon && typeof icon.renderCartBubble === 'function') {
        icon.renderCartBubble(count, false, false);
      }

      const { dialog } = this.refs;
      if (dialog) {
        dialog.classList.toggle('cart-drawer--empty', count === 0);
        const label = newDialog.getAttribute('aria-labelledby');
        if (label) dialog.setAttribute('aria-labelledby', label);
      }

      this.#updateStickyState();
      this.#announceCartCount(count);
    } catch (error) {
      if (!controller.signal.aborted) console.warn('Unable to refresh cart drawer', error);
    } finally {
      if (this.#cartRefreshController === controller) this.#cartRefreshController = null;
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    this.#cartRefreshVersion++;
    this.#cartRefreshController?.abort();
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
